import { HttpParams } from '@angular/common/http';
import { BasiliskJobMetadata } from 'modules/basilisk/basilisk.model';
import { JOB_STATUS_LABEL } from 'services/jobs/jobs.const';
import {
	CreatedJob,
	Job,
	JobProductSlug,
	JobsOverview,
	JobStatus,
	JobStatusLabel,
} from 'services/jobs/jobs.model';
import { MockHttpError, MockRoute, mockRoute, newMockId, nowIso, paginate } from '../mock-api';
import { MOCK_CATALOG } from '../shared/mock-context.const';
import { findJobEntry, registerCreatedJob, setJobStatus } from '../shared/mock-job-state';
import { MockJobRosterEntry } from '../shared/mock-jobs-roster.const';
import {
	buildMonthlyUsage,
	CREATED_CADASTRO_RESULT,
	jobResultOf,
	jobView,
	markProcessed,
	markRetryStarted,
	MockJobDocumentSeed,
	MockJobView,
	parseDateMs,
	PRODUCT_SUCCESS_STATUS,
	productOfSetup,
	queryJobViews,
	registerCreatedProfile,
	sessionContext,
	toJob,
	toJobDetail,
	toResultFields,
} from './jobs.const';

/** Tempo simulado do pipeline de IA para jobs criados na demo */
const CREATE_PIPELINE_MS = 6_000;
const RETRY_PIPELINE_MS = 5_000;

const ALL_STATUS_LABELS: JobStatusLabel[] = [
	'Aguardando',
	'Em processamento',
	'Aguardando revisão',
	'Em revisão',
	'Finalizado',
	'Falha',
];

// ── Helpers de request ──────────────────────────────────────────────────────

interface ResolvedScope {
	product: JobProductSlug | null;
	productSetupId: string | null;
}

/** Produto da request: `product` › `productSetupId` › contexto ativo da sessão */
function resolveScope(query: HttpParams): ResolvedScope {
	const productParam = query.get('product');
	const requestedSetupId = query.get('productSetupId') ?? sessionContext().productSetupId;
	const product =
		productParam && productParam in MOCK_CATALOG
			? (productParam as JobProductSlug)
			: productOfSetup(requestedSetupId);
	// Setup de outro produto (ex.: contexto ativo em Cadastro listando Basilisk) não restringe a lista
	const productSetupId = productOfSetup(requestedSetupId) === product ? requestedSetupId : null;
	return { product, productSetupId };
}

function statusFilterOf(query: HttpParams): ReadonlySet<JobStatusLabel> | null {
	const status = query.get('status');
	if (!status) return null;
	const label = JOB_STATUS_LABEL[status as JobStatus] ?? (status as JobStatusLabel);
	return new Set<JobStatusLabel>([label]);
}

function sortJobs(items: Job[], query: HttpParams): Job[] {
	const sortBy = query.get('sortBy');
	if (!sortBy) return items;
	const direction = query.get('sortOrder') === 'asc' ? 1 : -1;
	const valueOf = (job: Job): string | number => {
		switch (sortBy) {
			case 'status':
				return job.status;
			case 'createdBy':
				return job.createdBy.name.toLowerCase();
			case 'description':
				return (job.description ?? '').toLowerCase();
			case 'pagesProcessed':
				return job.pagesProcessed;
			default:
				return new Date(job.createdAt).getTime();
		}
	};
	return [...items].sort((first, second) => {
		const firstValue = valueOf(first);
		const secondValue = valueOf(second);
		if (firstValue < secondValue) return -1 * direction;
		if (firstValue > secondValue) return 1 * direction;
		return 0;
	});
}

function requireView(jobId: string): MockJobView {
	const entry = findJobEntry(jobId);
	if (!entry) throw new MockHttpError(404, 'Job não encontrado', `Nenhum job com o id ${jobId}.`);
	return jobView(entry);
}

function successResultOf(view: MockJobView): Record<string, unknown> {
	return jobResultOf({ ...view, status: PRODUCT_SUCCESS_STATUS[view.entry.product] }) ?? {};
}

// ── POST /jobs ──────────────────────────────────────────────────────────────

function parseMetadata(raw: FormDataEntryValue | null): BasiliskJobMetadata | undefined {
	if (typeof raw !== 'string' || !raw) return undefined;
	try {
		return JSON.parse(raw) as BasiliskJobMetadata;
	} catch {
		return undefined;
	}
}

function readDocuments(body: FormData): { seed: MockJobDocumentSeed; file: File }[] {
	const documents: { seed: MockJobDocumentSeed; file: File }[] = [];
	for (let index = 0; body.has(`documents[${index}].file`); index++) {
		const file = body.get(`documents[${index}].file`);
		const fileTypeId = body.get(`documents[${index}].fileTypeId`);
		if (!(file instanceof File)) continue;
		documents.push({
			file,
			seed: {
				filename: file.name,
				fileTypeId: typeof fileTypeId === 'string' ? fileTypeId : '',
				pageCount: 1 + Math.floor(Math.random() * 6),
				sizeBytes: file.size,
			},
		});
	}
	return documents;
}

function createJob(body: unknown): CreatedJob {
	if (!(body instanceof FormData)) {
		throw new MockHttpError(
			400,
			'Requisição inválida',
			'Envie os documentos como multipart/form-data.',
		);
	}

	const productSetupId = String(body.get('productSetupId') ?? '');
	const product = productOfSetup(productSetupId);
	if (!product) {
		throw new MockHttpError(
			422,
			'Configuração de produto inválida',
			`productSetupId "${productSetupId}" não encontrado.`,
		);
	}

	const documents = readDocuments(body);
	if (documents.length === 0) {
		throw new MockHttpError(
			422,
			'Nenhum documento enviado',
			'Envie ao menos um documento para criar o job.',
		);
	}

	const rawDescription = body.get('description');
	const typedDescription = typeof rawDescription === 'string' ? rawDescription.trim() : '';
	const description = typedDescription || documents[0].seed.filename;
	const metadata = parseMetadata(body.get('metadata'));

	const id = newMockId('job');
	const createdAtMs = Date.now() - 900;
	const entry: MockJobRosterEntry = {
		id,
		product,
		productSetupId,
		status: 'Em processamento',
		description,
		createdDaysAgo: 0,
		createdBy: sessionContext().personKey,
		source: 'UI',
	};

	registerCreatedJob(entry);
	registerCreatedProfile(
		id,
		{
			minutesAgo: 0,
			queueMs: 900,
			processingMs: CREATE_PIPELINE_MS,
			reviewMs: 0,
			documents: documents.map((document) => document.seed),
			metadata,
			result: product === 'cadastro' ? CREATED_CADASTRO_RESULT : undefined,
		},
		createdAtMs,
	);
	// Estado inicial também passa pelo stream: a caixa de entrada já aberta recarrega
	setJobStatus(id, 'Em processamento');

	setTimeout(() => {
		markProcessed(id);
		const view = jobView(entry);
		setJobStatus(id, PRODUCT_SUCCESS_STATUS[product], successResultOf(view));
	}, CREATE_PIPELINE_MS);

	return {
		id,
		status: JobStatus.PROCESSING,
		source: 'UI',
		description: typedDescription || null,
		createdAt: new Date(createdAtMs).toISOString(),
		startedAt: nowIso(),
		documents: documents.map(({ seed }, index) => ({
			fileId: `file-${id}-${index + 1}`,
			filename: seed.filename,
			mimeType: documents[index].file.type || 'application/pdf',
			sizeBytes: seed.sizeBytes,
			sizeBytesRaw: String(seed.sizeBytes),
			pageCount: null,
		})),
	};
}

// ── POST /jobs/:id/retry ────────────────────────────────────────────────────

const RETRYABLE_STATUSES: ReadonlySet<JobStatusLabel> = new Set<JobStatusLabel>([
	'Falha',
	'Finalizado',
]);

function retryJob(jobId: string): null {
	const view = requireView(jobId);
	if (!RETRYABLE_STATUSES.has(view.status)) {
		throw new MockHttpError(
			409,
			'Job em andamento',
			`O job está "${view.status}" e só pode ser reprocessado após finalizar ou falhar.`,
		);
	}

	markRetryStarted(jobId, view.createdAtMs);
	setJobStatus(jobId, 'Em processamento');

	setTimeout(() => {
		markProcessed(jobId);
		setJobStatus(
			jobId,
			PRODUCT_SUCCESS_STATUS[view.entry.product],
			successResultOf(requireView(jobId)),
		);
	}, RETRY_PIPELINE_MS);

	return null;
}

// ── GET /jobs/:id/result/download ───────────────────────────────────────────

function escapeXml(value: string): string {
	return value
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;');
}

function toXml(tag: string, value: unknown, indent: string): string {
	if (Array.isArray(value)) {
		return `${indent}<${tag}>\n${value.map((item) => toXml('item', item, `${indent}  `)).join('')}${indent}</${tag}>\n`;
	}
	if (typeof value === 'object' && value !== null) {
		const children = Object.entries(value)
			.map(([key, child]) => toXml(key, child, `${indent}  `))
			.join('');
		return `${indent}<${tag}>\n${children}${indent}</${tag}>\n`;
	}
	return `${indent}<${tag}>${escapeXml(String(value ?? ''))}</${tag}>\n`;
}

function toCsvCell(value: unknown): string {
	const text =
		typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value ?? '');
	return `"${text.replace(/"/g, '""')}"`;
}

function downloadResult(jobId: string, format: string | null): Blob {
	const view = requireView(jobId);
	if (view.status !== 'Finalizado') {
		throw new MockHttpError(
			409,
			'Resultado indisponível',
			'O resultado só pode ser baixado após o job ser finalizado.',
		);
	}
	const result = jobResultOf(view) ?? {};

	switch (format) {
		case 'xml':
			return new Blob(
				[`<?xml version="1.0" encoding="UTF-8"?>\n${toXml('resultado', result, '')}`],
				{
					type: 'application/xml',
				},
			);
		case 'xlsx': {
			const rows = Object.entries(result).map(
				([key, value]) => `${toCsvCell(key)};${toCsvCell(value)}`,
			);
			return new Blob([`﻿"campo";"valor"\n${rows.join('\n')}`], {
				type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
			});
		}
		default:
			return new Blob([JSON.stringify({ jobId, status: view.status, result }, null, 2)], {
				type: 'application/json',
			});
	}
}

// ── Rotas ───────────────────────────────────────────────────────────────────

export const JOBS_MOCK_ROUTES: MockRoute[] = [
	mockRoute('GET', '/jobs/overview', ({ query }): JobsOverview => {
		const scope = resolveScope(query);
		const views = queryJobViews({
			product: scope.product,
			productSetupId: scope.productSetupId,
			source: query.get('source'),
			createdByUserId: query.get('createdBy'),
			fromMs: parseDateMs(query.get('dateFrom')),
			toMs: parseDateMs(query.get('dateTo')),
			search: query.get('search'),
		});
		const byStatus: Partial<Record<JobStatusLabel, number>> = {};
		for (const label of ALL_STATUS_LABELS) {
			byStatus[label] = views.filter((view) => view.status === label).length;
		}
		return {
			total: views.length,
			byStatus,
			monthlyUsage: buildMonthlyUsage(scope.productSetupId, scope.product),
			generatedAt: nowIso(),
		};
	}),

	mockRoute('GET', '/jobs', ({ query }) => {
		const scope = resolveScope(query);
		const views = queryJobViews({
			product: scope.product,
			productSetupId: scope.productSetupId,
			statuses: statusFilterOf(query),
			source: query.get('source'),
			createdByUserId: query.get('createdBy'),
			fromMs: parseDateMs(query.get('dateFrom')),
			toMs: parseDateMs(query.get('dateTo')),
			search: query.get('search'),
		});
		const include = query.get('include');
		return paginate(
			sortJobs(
				views.map((view) => toJob(view, include)),
				query,
			),
			query,
		);
	}),

	mockRoute('POST', '/jobs', ({ body }) => createJob(body)),

	mockRoute('GET', '/jobs/:id', ({ params }) => toJobDetail(requireView(params['id']))),

	mockRoute('GET', '/jobs/:id/status', ({ params }) => {
		const view = requireView(params['id']);
		return {
			id: view.entry.id,
			status: view.status,
			startedAt: view.startedAt,
			finishedAt: view.finishedAt,
			timings: view.timings,
		};
	}),

	mockRoute('GET', '/jobs/:id/result', ({ params }) => {
		const view = requireView(params['id']);
		if (view.entry.product === 'cadastro') {
			if (view.status !== 'Finalizado') {
				throw new MockHttpError(409, 'Resultado indisponível', 'O job ainda não foi finalizado.');
			}
			return toResultFields(view);
		}
		return jobResultOf(view) ?? {};
	}),

	mockRoute('GET', '/jobs/:id/result/download', ({ params, query }) =>
		downloadResult(params['id'], query.get('format')),
	),

	mockRoute('POST', '/jobs/:id/retry', ({ params }) => retryJob(params['id'])),
];
